import React, { useEffect, useState, useCallback } from 'react'
import { HiDuplicate } from 'react-icons/hi'
import { toast } from 'react-hot-toast'
import { useParams, useNavigate } from 'react-router-dom'
import { getDeviceId, api, ApiError } from '../utils/api'
import logger from '../utils/logger'
import { trackPollViewed, trackPollLinkCopied, trackCurrencyChanged, trackErrorCurrencySave } from '../utils/analytics'
import usePageTitle from '../hooks/usePageTitle'
import useAuth from '../hooks/useAuth'

// Import custom hooks
import usePollData from '../hooks/usePollData'
import useRealtimeSubscription from '../hooks/useRealtimeSubscription'
import useVoteManagement from '../hooks/useVoteManagement'
import useChoiceManagement from '../hooks/useChoiceManagement'
import useClonePoll from '../hooks/useClonePoll'
import useEditMode from '../hooks/useEditMode'

// Import components
import PollHeader from './poll/PollHeader'
import ChoiceList from './poll/ChoiceList'
import AddChoiceForm from './poll/AddChoiceForm'
import ExportModal from './poll/ExportModal'
import SkeletonLoader from './SkeletonLoader'
import './PollPage.css'

function PollPage() {
  const { id: pollId } = useParams()
  const navigate = useNavigate()

  // Initialize device ID and get authenticated user
  const deviceId = getDeviceId()
  const { user } = useAuth()
  const userId = user?.id || null

  // Use custom hooks
  const { poll, setPoll, choices, loading, error, fetchPoll, setChoices } = usePollData(pollId)

  // Set page title based on poll title (dynamic)
  usePageTitle(poll?.title || 'Poll')
  const { realtimeStatus, activeViewers, reconnect, pendingVotesRef, pendingRemovalsRef } = useRealtimeSubscription(pollId, poll, choices, setChoices, setPoll, deviceId)
  const { votingStates, handleVote, handleToggleVote, handleRemoveVote } = useVoteManagement(pollId, choices, setChoices, deviceId, pendingVotesRef, pendingRemovalsRef)
  const {
    newChoiceText,
    setNewChoiceText,
    newChoicePrice,
    setNewChoicePrice,
    newChoiceOptions,
    setNewChoiceOptions,
    addingChoice,
    addChoiceError,
    handleAddChoice
  } = useChoiceManagement(poll, choices, setChoices)
  const {
    cloneMode,
    selectedChoices,
    showCloneModal,
    clonePollName,
    isCloning,
    setClonePollName,
    toggleCloneMode,
    toggleChoiceSelection,
    handleClonePoll,
    openCloneModal,
    closeCloneModal
  } = useClonePoll(choices, navigate)

  // Edit mode hook
  const {
    editMode,
    editingChoiceId,
    editingChoiceText,
    setEditingChoiceText,
    editingPollTitle,
    editingTitleText,
    setEditingTitleText,
    isUpdating,
    isPollCreator,
    canEditPoll,
    canEditChoice,
    hasEditableChoices,
    toggleEditMode: toggleEditModeHook,
    startEditingPollTitle,
    cancelEditingPollTitle,
    savePollTitle,
    startEditingChoice,
    cancelEditingChoice,
    saveChoiceText,
    deleteChoice
  } = useEditMode(pollId, poll, choices, setChoices, setPoll)

  // Effect to fetch poll data on mount
  useEffect(() => {
    fetchPoll()
  }, [pollId, fetchPoll])

  // Track poll viewed when data loads
  useEffect(() => {
    if (poll && choices && !loading && !error) {
      trackPollViewed(poll.id, choices.length)
    }
  }, [poll, choices, loading, error])

  // Currency state management
  const [currency, setCurrency] = useState(null)
  const [currencyConfirmed, setCurrencyConfirmed] = useState(false)

  // Sync currency from poll data
  useEffect(() => {
    if (poll) {
      setCurrency(poll.currency || null)
      // If poll already has currency set (from previous session), consider it confirmed
      // Also if poll is not owned by current user, don't show currency selector
      if (poll.currency !== undefined || !isPollCreator) {
        setCurrencyConfirmed(true)
      }
    }
  }, [poll, isPollCreator])

  // Handle currency change and auto-save
  const handleCurrencyChange = useCallback(async (newCurrency) => {
    setCurrency(newCurrency)

    // Auto-save to API with the new value (not from state which hasn't updated yet)
    try {
      logger.info('Saving currency setting', { pollId, currency: newCurrency })

      await api.request(`/api/polls/${pollId}`, {
        method: 'PATCH',
        body: JSON.stringify({ currency: newCurrency, device_id: deviceId })
      })

      setCurrencyConfirmed(true)
      setPoll(prev => prev ? { ...prev, currency: newCurrency } : prev)
      toast.success(newCurrency ? `Currency set to ${newCurrency}` : 'No currency selected')
      trackCurrencyChanged(pollId, newCurrency)
      logger.info('Currency saved', { pollId, currency: newCurrency })
    } catch (err) {
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to save currency'
      toast.error(errorMessage)
      trackErrorCurrencySave(pollId, errorMessage)
      logger.error('Failed to save currency', { error: errorMessage, pollId })
    }
  }, [pollId, deviceId, setPoll])

  // Handle currency confirmation (kept for backward compatibility)
  const handleCurrencyConfirm = useCallback(async () => {
    setCurrencyConfirmed(true)
  }, [])

  // Helper functions
  const shareUrl = `${window.location.origin}/poll/${pollId}`

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      toast.success('Link copied to clipboard!')
      trackPollLinkCopied(pollId)
      logger.info('Link copied to clipboard', { shareUrl })
    } catch (err) {
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea')
        textArea.value = shareUrl
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        toast.success('Link copied to clipboard!')
        trackPollLinkCopied(pollId)
        logger.info('Link copied to clipboard (fallback)', { shareUrl })
      } catch (fallbackErr) {
        toast.error('Failed to copy link. Please copy manually.')
        logger.error('Failed to copy link', { error: fallbackErr })
      }
    }
  }

  // Loading state
  if (loading) {
    return <SkeletonLoader variant="poll-page" />
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: '2rem' }} className="fade-in">
        <div className="card" style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ color: 'var(--error-color)' }}>Error</h2>
          <p style={{ color: 'var(--error-color)', marginBottom: '1rem', fontSize: '1.125rem' }}>{error}</p>
          <div className="space-y-4" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <button className="btn btn-primary" onClick={fetchPoll} style={{ width: '100%' }}>
              Try Again
            </button>
            <button className="btn btn-secondary" onClick={() => navigate('/')} style={{ width: '100%' }}>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="poll-container fade-in">
      <PollHeader
        poll={poll}
        pollId={pollId}
        choices={choices}
        realtimeStatus={realtimeStatus}
        activeViewers={activeViewers}
        deviceId={deviceId}
        onCopyLink={copyToClipboard}
        onReconnect={reconnect}
        onRefresh={fetchPoll}
        // Edit mode props
        editMode={editMode}
        editingPollTitle={editingPollTitle}
        editingTitleText={editingTitleText}
        setEditingTitleText={setEditingTitleText}
        isUpdating={isUpdating}
        canEditPoll={canEditPoll}
        isPollCreator={isPollCreator}
        hasEditableChoices={hasEditableChoices}
        onStartEditingTitle={startEditingPollTitle}
        onCancelEditingTitle={cancelEditingPollTitle}
        onSaveTitle={savePollTitle}
        onToggleEditMode={toggleEditModeHook}
        // Currency props
        currency={currency}
        currencyConfirmed={currencyConfirmed}
        onCurrencyChange={handleCurrencyChange}
        onCurrencyConfirm={handleCurrencyConfirm}
      />

      <ChoiceList
        choices={choices}
        votingStates={votingStates}
        cloneMode={cloneMode}
        selectedChoices={selectedChoices}
        onVote={handleVote}
        onToggleVote={handleToggleVote}
        onRemoveVote={handleRemoveVote}
        onSelectChoice={toggleChoiceSelection}
        onToggleCloneMode={toggleCloneMode}
        // Edit mode props
        editMode={editMode}
        editingChoiceId={editingChoiceId}
        editingChoiceText={editingChoiceText}
        setEditingChoiceText={setEditingChoiceText}
        isUpdating={isUpdating}
        canEditChoice={canEditChoice}
        isPollCreator={isPollCreator}
        onStartEditingChoice={startEditingChoice}
        onCancelEditingChoice={cancelEditingChoice}
        onSaveChoice={saveChoiceText}
        onDeleteChoice={deleteChoice}
        deviceId={deviceId}
        userId={userId}
        // Currency props for price display
        currency={currency}
      />

      <AddChoiceForm
        newChoiceText={newChoiceText}
        setNewChoiceText={setNewChoiceText}
        loading={addingChoice}
        error={addChoiceError}
        onSubmit={handleAddChoice}
        // Price/options props
        currency={currency}
        newChoicePrice={newChoicePrice}
        setNewChoicePrice={setNewChoicePrice}
        newChoiceOptions={newChoiceOptions}
        setNewChoiceOptions={setNewChoiceOptions}
      />

      <ExportModal
        show={showCloneModal}
        selectedCount={selectedChoices.size}
        pollName={clonePollName}
        setPollName={setClonePollName}
        loading={isCloning}
        onClose={closeCloneModal}
        onExport={handleClonePoll}
      />

      {selectedChoices.size > 0 && (
        <div className="fade-in-up" style={{
          position: 'fixed',
          bottom: '2rem',
          right: '2rem',
          zIndex: 1000
        }}>
          <button
            className={`btn btn-primary ${isCloning ? 'btn-loading' : ''}`}
            onClick={openCloneModal}
            disabled={isCloning}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              fontSize: '1rem',
              fontWeight: '600',
              boxShadow: '0 8px 32px rgba(59, 130, 246, 0.4)',
              borderRadius: 'var(--radius-lg)',
              transition: 'all 0.2s ease',
              minHeight: '48px',
              width: 'auto',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)'
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(59, 130, 246, 0.5)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)'
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(59, 130, 246, 0.4)'
            }}
          >
            <HiDuplicate style={{ fontSize: '1.1rem' }} />
            Create poll with {selectedChoices.size} choice{selectedChoices.size !== 1 ? 's' : ''}
          </button>
        </div>
      )}
    </div>
  )
}

export default PollPage